// ============================================
// OpenClaw v2.0 - 智能拓客系统
// 多源爬虫引擎 | 企业情报增强 | 批量触达
// ============================================

import React, { useState } from 'react';
import { 
  Search, MapPin, Download, Send, Plus, Check, X,
  Star, Users, TrendingUp, MessageSquare, Phone,
  Sparkles, Target, Zap, RefreshCw,
  Clock, DollarSign, Briefcase, Award, AlertCircle, Trash2,
  Edit3, Eye,
  BarChart3, Activity, Map as MapIcon
} from 'lucide-react';

// ==================== 类型定义 ====================

type BusinessType = 'maternity' | 'kids_store' | 'education' | 'kids_entertainment' | 'photography' | 'swimming' | 'dental' | 'hospital' | 'eyewear' | 'optical';
type LeadStatus = 'pending' | 'contacted' | 'following' | 'converted' | 'lost';
type BusinessTrend = 'expanding' | 'stable' | 'declining';

interface Lead {
  id: string;
  name: string;
  type: BusinessType;
  address: string;
  area: string;
  contact?: string;
  phone?: string;
  rating: number;
  reviewCount: number;
  avgPrice?: string;
  weeklyVisits: number;
  recruitCount: number;
  recruitAvgSalary?: string;
  businessTrend: BusinessTrend;
  status: LeadStatus;
  score: number;
  tags: string[];
  lastContact?: string;
  notes?: string;
  lat: number;
  lng: number;
}

interface CrawlerTask {
  id: string;
  name: string;
  centerAddress: string;
  radius: number; // km
  businessTypes: BusinessType[];
  maxResults: number;
  status: 'idle' | 'running' | 'completed' | 'failed';
  progress: number;
  leadsFound: number;
  createdAt: string;
}

interface MessageTemplate {
  id: string;
  name: string;
  type: 'first' | 'follow' | 'convert';
  content: string;
  variables: string[];
  replyRate: number;
}

// ==================== 模拟数据 ====================

const BUSINESS_TYPE_MAP: Record<BusinessType, { label: string; color: string; icon: string }> = {
  maternity: { label: '月子中心', color: 'pink', icon: '🏥' },
  kids_store: { label: '母婴店', color: 'blue', icon: '🍼' },
  education: { label: '早教机构', color: 'green', icon: '📚' },
  kids_entertainment: { label: '儿童乐园', color: 'yellow', icon: '🎠' },
  photography: { label: '儿童摄影', color: 'purple', icon: '📷' },
  swimming: { label: '婴儿游泳', color: 'cyan', icon: '🏊' },
  dental: { label: '儿童牙科', color: 'red', icon: '🦷' },
  hospital: { label: '妇幼保健院', color: 'rose', icon: '🏨' },
  eyewear: { label: '眼镜店', color: 'indigo', icon: '👓' },
  optical: { label: '视光中心', color: 'violet', icon: '👁️' },
};

const TREND_MAP: Record<BusinessTrend, { label: string; color: string; icon: any }> = {
  expanding: { label: '扩张期', color: 'green', icon: TrendingUp },
  stable: { label: '稳定期', color: 'blue', icon: Activity },
  declining: { label: '下滑期', color: 'red', icon: AlertCircle },
};

const MOCK_LEADS: Lead[] = [
  {
    id: 'L001',
    name: '爱宝贝月子会所',
    type: 'maternity',
    address: '朝阳区建国路88号SOHO现代城',
    area: '朝阳区-CBD',
    contact: '王经理',
    phone: '138****5678',
    rating: 4.8,
    reviewCount: 156,
    avgPrice: '¥68,000',
    weeklyVisits: 234,
    recruitCount: 5,
    recruitAvgSalary: '8-12K',
    businessTrend: 'expanding',
    status: 'pending',
    score: 92,
    tags: ['高端', '连锁', '口碑好'],
    lat: 39.91,
    lng: 116.47,
  },
  {
    id: 'L002',
    name: '金宝贝早教中心',
    type: 'education',
    address: '海淀区中关村大街27号中关村大厦',
    area: '海淀区-中关村',
    contact: '李校长',
    phone: '139****1234',
    rating: 4.6,
    reviewCount: 328,
    avgPrice: '¥380/课时',
    weeklyVisits: 456,
    recruitCount: 3,
    recruitAvgSalary: '6-10K',
    businessTrend: 'expanding',
    status: 'contacted',
    score: 88,
    tags: ['连锁品牌', '客流大', '家长群体'],
    lastContact: '2024-03-25',
    notes: '对方对儿童视力筛查合作感兴趣',
    lat: 39.98,
    lng: 116.31,
  },
  {
    id: 'L003',
    name: '小太阳儿童乐园',
    type: 'kids_entertainment',
    address: '通州区万达广场3楼',
    area: '通州区-万达商圈',
    contact: '张店长',
    phone: '137****9999',
    rating: 4.3,
    reviewCount: 89,
    avgPrice: '¥128/次',
    weeklyVisits: 189,
    recruitCount: 0,
    recruitAvgSalary: '-',
    businessTrend: 'stable',
    status: 'pending',
    score: 72,
    tags: ['商场店', '亲子客群'],
    lat: 39.91,
    lng: 116.66,
  },
  {
    id: 'L004',
    name: '幸福时光儿童摄影',
    type: 'photography',
    address: '西城区西单大悦城B1层',
    area: '西城区-西单',
    contact: '陈老板',
    phone: '136****7777',
    rating: 4.9,
    reviewCount: 567,
    avgPrice: '¥1,280/套',
    weeklyVisits: 345,
    recruitCount: 8,
    recruitAvgSalary: '10-15K',
    businessTrend: 'expanding',
    status: 'following',
    score: 95,
    tags: ['高端', '连锁', '异业合作首选'],
    lastContact: '2024-03-26',
    notes: '已发送合作方案，等待回复',
    lat: 39.91,
    lng: 116.37,
  },
  {
    id: 'L005',
    name: '优贝口腔儿童牙科',
    type: 'dental',
    address: '朝阳区望京SOHO T3',
    area: '朝阳区-望京',
    contact: '刘主任',
    phone: '135****8888',
    rating: 4.7,
    reviewCount: 234,
    avgPrice: '¥380/次',
    weeklyVisits: 178,
    recruitCount: 2,
    recruitAvgSalary: '12-18K',
    businessTrend: 'stable',
    status: 'converted',
    score: 85,
    tags: ['医疗背景', '专业可信', '已合作'],
    lastContact: '2024-03-20',
    notes: '成功签约，已开始互相引流',
    lat: 39.99,
    lng: 116.48,
  },
  {
    id: 'L006',
    name: '萌宝母婴生活馆',
    type: 'kids_store',
    address: '丰台区方庄桥东',
    area: '丰台区-方庄',
    contact: '赵姐',
    phone: '133****6666',
    rating: 4.2,
    reviewCount: 67,
    avgPrice: '¥200/单',
    weeklyVisits: 123,
    recruitCount: 1,
    recruitAvgSalary: '5-7K',
    businessTrend: 'declining',
    status: 'lost',
    score: 58,
    tags: ['社区店', '老店'],
    notes: '门店经营困难，暂时不考虑合作',
    lat: 39.86,
    lng: 116.43,
  },
  {
    id: 'L007',
    name: '蓝旗亲子游泳',
    type: 'swimming',
    address: '海淀区五道口购物中心',
    area: '海淀区-五道口',
    contact: '周经理',
    phone: '132****5555',
    rating: 4.5,
    reviewCount: 189,
    avgPrice: '¥268/次',
    weeklyVisits: 234,
    recruitCount: 4,
    recruitAvgSalary: '7-11K',
    businessTrend: 'expanding',
    status: 'pending',
    score: 82,
    tags: ['高端', '年轻父母', '高消费'],
    lat: 39.99,
    lng: 116.33,
  },
  {
    id: 'L008',
    name: '美中宜和妇儿医院',
    type: 'hospital',
    address: '朝阳区丽都商圈将台路',
    area: '朝阳区-丽都',
    contact: '市场总监',
    phone: '010-6413****',
    rating: 4.9,
    reviewCount: 1023,
    avgPrice: '产检¥15,000',
    weeklyVisits: 567,
    recruitCount: 12,
    recruitAvgSalary: '15-25K',
    businessTrend: 'expanding',
    status: 'contacted',
    score: 96,
    tags: ['高端医疗', '品牌强', '超级客户'],
    lastContact: '2024-03-24',
    notes: '正在对接市场部门，安排正式会议',
    lat: 39.97,
    lng: 116.49,
  },
];

const MOCK_TASKS: CrawlerTask[] = [
  {
    id: 'T001',
    name: 'CBD商圈拓客任务',
    centerAddress: '北京市朝阳区国贸',
    radius: 5,
    businessTypes: ['maternity', 'education', 'kids_entertainment'],
    maxResults: 50,
    status: 'completed',
    progress: 100,
    leadsFound: 23,
    createdAt: '2024-03-26 10:00',
  },
  {
    id: 'T002',
    name: '海淀教育区拓客',
    centerAddress: '北京市海淀区中关村',
    radius: 8,
    businessTypes: ['education', 'photography', 'dental'],
    maxResults: 100,
    status: 'running',
    progress: 65,
    leadsFound: 42,
    createdAt: '2024-03-27 09:30',
  },
];

const MESSAGE_TEMPLATES: MessageTemplate[] = [
  {
    id: 'TM001',
    name: '首次接触-儿童筛查合作',
    type: 'first',
    content: '您好{{name}}，我是{{company}}的负责人。我们注意到贵机构在{{industry}}领域做得很出色，服务了很多家长。\n\n我们是一家专业的儿童视力筛查机构，希望能与贵机构合作，为您的客户提供免费的儿童视力检查服务，同时也可以为贵机构增加一项增值服务。\n\n方便的话，我可以发一份详细的合作方案给您参考？',
    variables: ['name', 'company', 'industry'],
    replyRate: 0.18,
  },
  {
    id: 'TM002',
    name: '跟进-方案已发送',
    type: 'follow',
    content: '{{name}}您好，上次发给您的合作方案收到了吗？\n\n{{similar_company}}已经跟我们合作了3个月，他们的客户反馈很好，还带来了不少新客户。\n\n您看这周方便安排个15分钟的电话沟通吗？',
    variables: ['name', 'similar_company'],
    replyRate: 0.28,
  },
  {
    id: 'TM003',
    name: '转化-限时优惠',
    type: 'convert',
    content: '{{name}}您好！\n\n我们本月针对{{industry}}推出专属合作优惠：\n✓ 免费提供专业视力筛查设备\n✓ 专属客户经理全程支持\n✓ 联合营销活动策划\n\n这个优惠月底截止，如果您有兴趣，我可以先帮您预留名额？',
    variables: ['name', 'industry'],
    replyRate: 0.35,
  },
  {
    id: 'TM004',
    name: '异业合作-互相引流',
    type: 'first',
    content: '您好{{name}}，我是{{company}}的。\n\n我们两家服务的客群很相似，都是注重孩子健康的家长。我觉得咱们可以互相引流，比如在我们门店放置贵机构的宣传资料，您那边也可以帮我们推广视力检查服务。\n\n互相帮衬，一起把生意做大😊',
    variables: ['name', 'company'],
    replyRate: 0.22,
  },
];

// ==================== 主组件 ====================

export const OpenClawModule: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'dashboard' | 'tasks' | 'leads' | 'map' | 'messages'>('dashboard');
  const [leads] = useState<Lead[]>(MOCK_LEADS);
  const [tasks, setTasks] = useState<CrawlerTask[]>(MOCK_TASKS);
  const [selectedLeads, setSelectedLeads] = useState<Set<string>>(new Set());
  const [filterStatus, setFilterStatus] = useState<LeadStatus | 'all'>('all');
  const [filterType, setFilterType] = useState<BusinessType | 'all'>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [showNewTaskModal, setShowNewTaskModal] = useState(false);
  const [showMessageModal, setShowMessageModal] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<MessageTemplate>(MESSAGE_TEMPLATES[0]);

  // 筛选线索
  const filteredLeads = leads.filter(lead => {
    if (filterStatus !== 'all' && lead.status !== filterStatus) return false;
    if (filterType !== 'all' && lead.type !== filterType) return false;
    if (searchTerm && !lead.name.includes(searchTerm) && !lead.area.includes(searchTerm)) return false;
    return true;
  });

  // 统计数据
  const stats = {
    total: leads.length,
    pending: leads.filter(l => l.status === 'pending').length,
    contacted: leads.filter(l => l.status === 'contacted').length,
    following: leads.filter(l => l.status === 'following').length,
    converted: leads.filter(l => l.status === 'converted').length,
    highScore: leads.filter(l => l.score >= 85).length,
    expanding: leads.filter(l => l.businessTrend === 'expanding').length,
  };

  // 切换线索选中状态
  const toggleLeadSelection = (id: string) => {
    const newSet = new Set(selectedLeads);
    if (newSet.has(id)) newSet.delete(id);
    else newSet.add(id);
    setSelectedLeads(newSet);
  };

  // 全选/取消全选
  const toggleSelectAll = () => {
    if (selectedLeads.size === filteredLeads.length) {
      setSelectedLeads(new Set());
    } else {
      setSelectedLeads(new Set(filteredLeads.map(l => l.id)));
    }
  };

  // 导出CSV
  const exportCSV = () => {
    const selected = leads.filter(l => selectedLeads.has(l.id));
    const csv = [
      '名称,类型,地址,联系人,电话,评分,业务趋势,状态',
      ...selected.map(l => `${l.name},${BUSINESS_TYPE_MAP[l.type].label},${l.address},${l.contact || ''},${l.phone || ''},${l.rating},${TREND_MAP[l.businessTrend].label},${l.status}`)
    ].join('\n');
    
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `openclaw_leads_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    alert(`已导出 ${selected.length} 条线索到CSV`);
  };

  // 渲染标签页内容
  const renderContent = () => {
    switch (activeTab) {
      case 'dashboard':
        return <DashboardView stats={stats} leads={leads} />;
      case 'tasks':
        return <TasksView tasks={tasks} onNewTask={() => setShowNewTaskModal(true)} />;
      case 'leads':
        return (
          <LeadsView
            leads={filteredLeads}
            selectedLeads={selectedLeads}
            toggleLeadSelection={toggleLeadSelection}
            toggleSelectAll={toggleSelectAll}
            filterStatus={filterStatus}
            setFilterStatus={setFilterStatus}
            filterType={filterType}
            setFilterType={setFilterType}
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            onExport={exportCSV}
            onBatchMessage={() => setShowMessageModal(true)}
          />
        );
      case 'map':
        return <MapView leads={filteredLeads} />;
      case 'messages':
        return <MessagesView templates={MESSAGE_TEMPLATES} />;
      default:
        return null;
    }
  };

  return (
    <div className="h-full flex flex-col">
      {/* 顶部导航标签 */}
      <div className="bg-white border-b border-gray-200 px-6 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-6">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-purple-600 to-blue-600 flex items-center justify-center">
                <Zap className="w-5 h-5 text-white" />
              </div>
              <span className="font-bold text-gray-900">OpenClaw</span>
            </div>
            <nav className="flex gap-1">
              {[
                { id: 'dashboard', label: '数据概览', icon: BarChart3 },
                { id: 'tasks', label: '爬虫任务', icon: Target },
                { id: 'leads', label: '线索管理', icon: Users },
                { id: 'map', label: '地图选点', icon: MapIcon },
                { id: 'messages', label: '触达模板', icon: MessageSquare },
              ].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id as any)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    activeTab === tab.id
                      ? 'bg-purple-50 text-purple-600'
                      : 'text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  <tab.icon className="w-4 h-4" />
                  {tab.label}
                </button>
              ))}
            </nav>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-sm text-gray-500">AI Agent状态:</span>
            <span className="flex items-center gap-1.5 px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm">
              <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
              运行中
            </span>
          </div>
        </div>
      </div>

      {/* 内容区域 */}
      <div className="flex-1 overflow-auto bg-gray-50 p-6">
        {renderContent()}
      </div>

      {/* 新建任务弹窗 */}
      {showNewTaskModal && (
        <NewTaskModal
          onClose={() => setShowNewTaskModal(false)}
          onCreate={(task) => {
            setTasks([task, ...tasks]);
            setShowNewTaskModal(false);
          }}
        />
      )}

      {/* 批量私信弹窗 */}
      {showMessageModal && (
        <MessageModal
          selectedCount={selectedLeads.size}
          templates={MESSAGE_TEMPLATES}
          selectedTemplate={selectedTemplate}
          onSelectTemplate={setSelectedTemplate}
          onClose={() => setShowMessageModal(false)}
          onSend={() => {
            alert(`已向 ${selectedLeads.size} 个客户发送私信`);
            setShowMessageModal(false);
            setSelectedLeads(new Set());
          }}
        />
      )}
    </div>
  );
};

// ==================== 子组件 ====================

// 1. 数据概览
const DashboardView: React.FC<{ stats: any; leads: Lead[] }> = ({ stats, leads }) => {
  const statusData = [
    { label: '待联系', value: stats.pending, color: 'bg-gray-400' },
    { label: '已联系', value: stats.contacted, color: 'bg-blue-500' },
    { label: '跟进中', value: stats.following, color: 'bg-yellow-500' },
    { label: '已转化', value: stats.converted, color: 'bg-green-500' },
  ];

  const trendData = [
    { label: '扩张期', value: stats.expanding, color: 'bg-green-500', icon: TrendingUp },
    { label: '稳定期', value: leads.filter(l => l.businessTrend === 'stable').length, color: 'bg-blue-500', icon: Activity },
    { label: '下滑期', value: leads.filter(l => l.businessTrend === 'declining').length, color: 'bg-red-500', icon: AlertCircle },
  ];

  return (
    <div className="space-y-6 max-w-7xl mx-auto">
      {/* 核心指标 */}
      <div className="grid grid-cols-4 gap-4">
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-500 text-sm">总线索数</p>
              <p className="text-3xl font-bold text-gray-900 mt-1">{stats.total}</p>
            </div>
            <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
              <Users className="w-6 h-6 text-purple-600" />
            </div>
          </div>
          <p className="text-sm text-green-600 mt-2">+23 本周新增</p>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-500 text-sm">高意向线索</p>
              <p className="text-3xl font-bold text-gray-900 mt-1">{stats.highScore}</p>
            </div>
            <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
              <Target className="w-6 h-6 text-green-600" />
            </div>
          </div>
          <p className="text-sm text-green-600 mt-2">评分85+</p>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-500 text-sm">扩张期企业</p>
              <p className="text-3xl font-bold text-gray-900 mt-1">{stats.expanding}</p>
            </div>
            <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
              <TrendingUp className="w-6 h-6 text-blue-600" />
            </div>
          </div>
          <p className="text-sm text-blue-600 mt-2">优先触达</p>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-500 text-sm">本月转化率</p>
              <p className="text-3xl font-bold text-gray-900 mt-1">18.5%</p>
            </div>
            <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center">
              <Award className="w-6 h-6 text-orange-600" />
            </div>
          </div>
          <p className="text-sm text-green-600 mt-2">+2.3% 环比</p>
        </div>
      </div>

      {/* 数据分布 */}
      <div className="grid grid-cols-2 gap-6">
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <h3 className="font-semibold text-gray-900 mb-4">线索状态分布</h3>
          <div className="space-y-3">
            {statusData.map((item) => (
              <div key={item.label}>
                <div className="flex items-center justify-between text-sm mb-1">
                  <span className="text-gray-600">{item.label}</span>
                  <span className="font-medium">{item.value} ({((item.value / stats.total) * 100).toFixed(0)}%)</span>
                </div>
                <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                  <div className={`h-full ${item.color} rounded-full`} style={{ width: `${(item.value / stats.total) * 100}%` }}></div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <h3 className="font-semibold text-gray-900 mb-4">企业健康度分析</h3>
          <div className="space-y-4">
            {trendData.map((item) => (
              <div key={item.label} className="flex items-center gap-4">
                <div className={`w-10 h-10 rounded-lg ${item.color} bg-opacity-20 flex items-center justify-center`}>
                  <item.icon className={`w-5 h-5 ${item.color.replace('bg-', 'text-')}`} />
                </div>
                <div className="flex-1">
                  <div className="flex items-center justify-between text-sm mb-1">
                    <span className="text-gray-600">{item.label}</span>
                    <span className="font-medium">{item.value} 家</span>
                  </div>
                  <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                    <div className={`h-full ${item.color} rounded-full`} style={{ width: `${(item.value / stats.total) * 100}%` }}></div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* 最近线索 */}
      <div className="bg-white rounded-xl border border-gray-200 p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold text-gray-900">最近发现的高意向线索</h3>
          <button className="text-sm text-purple-600 hover:underline">查看全部</button>
        </div>
        <div className="grid grid-cols-4 gap-4">
          {leads.filter(l => l.score >= 85).slice(0, 4).map((lead) => (
            <div key={lead.id} className="border border-gray-200 rounded-lg p-4 hover:border-purple-300 transition-colors">
              <div className="flex items-start justify-between mb-2">
                <span className="text-2xl">{BUSINESS_TYPE_MAP[lead.type].icon}</span>
                <span className="px-2 py-0.5 bg-green-100 text-green-700 rounded text-xs font-bold">
                  {lead.score}分
                </span>
              </div>
              <h4 className="font-medium text-gray-900 truncate">{lead.name}</h4>
              <p className="text-xs text-gray-500 mb-2">{lead.area}</p>
              <div className="flex items-center gap-2 text-xs text-gray-600">
                <Star className="w-3 h-3 text-yellow-500" />
                <span>{lead.rating} ({lead.reviewCount}评)</span>
              </div>
              {lead.recruitCount > 0 && (
                <div className="flex items-center gap-2 text-xs text-green-600 mt-1">
                  <Briefcase className="w-3 h-3" />
                  <span>招{lead.recruitCount}人 {lead.recruitAvgSalary}</span>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// 2. 爬虫任务
const TasksView: React.FC<{ tasks: CrawlerTask[]; onNewTask: () => void }> = ({ tasks, onNewTask }) => {
  return (
    <div className="max-w-7xl mx-auto space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">爬虫任务管理</h2>
          <p className="text-sm text-gray-500 mt-1">配置多源爬虫任务，自动发现潜在客户</p>
        </div>
        <button
          onClick={onNewTask}
          className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
        >
          <Plus className="w-4 h-4" />
          新建任务
        </button>
      </div>

      <div className="grid gap-4">
        {tasks.map((task) => (
          <div key={task.id} className="bg-white rounded-xl border border-gray-200 p-5">
            <div className="flex items-start justify-between">
              <div className="flex items-start gap-4">
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                  task.status === 'running' ? 'bg-blue-100' :
                  task.status === 'completed' ? 'bg-green-100' :
                  task.status === 'failed' ? 'bg-red-100' :
                  'bg-gray-100'
                }`}>
                  {task.status === 'running' ? (
                    <RefreshCw className="w-6 h-6 text-blue-600 animate-spin" />
                  ) : task.status === 'completed' ? (
                    <Check className="w-6 h-6 text-green-600" />
                  ) : task.status === 'failed' ? (
                    <X className="w-6 h-6 text-red-600" />
                  ) : (
                    <Clock className="w-6 h-6 text-gray-600" />
                  )}
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">{task.name}</h3>
                  <div className="flex items-center gap-4 text-sm text-gray-500 mt-1">
                    <span className="flex items-center gap-1">
                      <MapPin className="w-3.5 h-3.5" />
                      {task.centerAddress}
                    </span>
                    <span className="flex items-center gap-1">
                      <Target className="w-3.5 h-3.5" />
                      {task.radius}km
                    </span>
                    <span className="flex items-center gap-1">
                      <Clock className="w-3.5 h-3.5" />
                      {task.createdAt}
                    </span>
                  </div>
                  <div className="flex flex-wrap gap-1 mt-2">
                    {task.businessTypes.map((type) => (
                      <span key={type} className="px-2 py-0.5 bg-gray-100 text-gray-600 rounded text-xs">
                        {BUSINESS_TYPE_MAP[type].icon} {BUSINESS_TYPE_MAP[type].label}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
              <div className="text-right">
                <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                  task.status === 'running' ? 'bg-blue-100 text-blue-700' :
                  task.status === 'completed' ? 'bg-green-100 text-green-700' :
                  task.status === 'failed' ? 'bg-red-100 text-red-700' :
                  'bg-gray-100 text-gray-700'
                }`}>
                  {task.status === 'running' ? '进行中' :
                   task.status === 'completed' ? '已完成' :
                   task.status === 'failed' ? '失败' : '待启动'}
                </span>
                <p className="text-2xl font-bold text-gray-900 mt-2">{task.leadsFound}</p>
                <p className="text-xs text-gray-500">发现线索</p>
              </div>
            </div>
            {task.status === 'running' && (
              <div className="mt-4">
                <div className="flex items-center justify-between text-sm mb-1">
                  <span className="text-gray-600">抓取进度</span>
                  <span className="font-medium">{task.progress}%</span>
                </div>
                <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                  <div className="h-full bg-blue-500 rounded-full transition-all" style={{ width: `${task.progress}%` }}></div>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* 数据源说明 */}
      <div className="bg-gradient-to-r from-purple-50 to-blue-50 rounded-xl border border-purple-200 p-5">
        <h3 className="font-semibold text-purple-900 mb-3 flex items-center gap-2">
          <Sparkles className="w-5 h-5" />
          多源数据融合引擎
        </h3>
        <div className="grid grid-cols-4 gap-4">
          {[
            { name: '高德地图', desc: '位置、POI、商户信息', icon: '🗺️' },
            { name: '大众点评', desc: '评分、评论、价格', icon: '⭐' },
            { name: '招聘平台', desc: '招聘动态、薪资水平', icon: '💼' },
            { name: 'AI增强', desc: '趋势分析、意图识别', icon: '🤖' },
          ].map((source) => (
            <div key={source.name} className="bg-white rounded-lg p-3 border border-purple-100">
              <span className="text-2xl">{source.icon}</span>
              <h4 className="font-medium text-gray-900 mt-1">{source.name}</h4>
              <p className="text-xs text-gray-500">{source.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// 3. 线索管理
const LeadsView: React.FC<{
  leads: Lead[];
  selectedLeads: Set<string>;
  toggleLeadSelection: (id: string) => void;
  toggleSelectAll: () => void;
  filterStatus: LeadStatus | 'all';
  setFilterStatus: (s: LeadStatus | 'all') => void;
  filterType: BusinessType | 'all';
  setFilterType: (t: BusinessType | 'all') => void;
  searchTerm: string;
  setSearchTerm: (s: string) => void;
  onExport: () => void;
  onBatchMessage: () => void;
}> = ({
  leads, selectedLeads, toggleLeadSelection, toggleSelectAll,
  filterStatus, setFilterStatus, filterType, setFilterType,
  searchTerm, setSearchTerm, onExport, onBatchMessage
}) => {
  return (
    <div className="max-w-7xl mx-auto space-y-4">
      {/* 筛选栏 */}
      <div className="bg-white rounded-xl border border-gray-200 p-4">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 flex-1">
            <div className="relative flex-1 max-w-xs">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="搜索名称、区域..."
                className="w-full pl-9 pr-4 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-purple-500"
              />
            </div>
            
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value as any)}
              className="px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-purple-500"
            >
              <option value="all">全部状态</option>
              <option value="pending">待联系</option>
              <option value="contacted">已联系</option>
              <option value="following">跟进中</option>
              <option value="converted">已转化</option>
              <option value="lost">已流失</option>
            </select>

            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value as any)}
              className="px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-purple-500"
            >
              <option value="all">全部类型</option>
              {Object.entries(BUSINESS_TYPE_MAP).map(([key, value]) => (
                <option key={key} value={key}>{value.icon} {value.label}</option>
              ))}
            </select>
          </div>

          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-500">已选 {selectedLeads.size} 个</span>
            <button
              onClick={toggleSelectAll}
              className="px-3 py-2 border border-gray-200 rounded-lg text-sm hover:border-purple-500 transition-colors"
            >
              {selectedLeads.size === leads.length ? '取消全选' : '全选'}
            </button>
            <button
              onClick={onBatchMessage}
              disabled={selectedLeads.size === 0}
              className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <Send className="w-4 h-4" />
              批量私信
            </button>
            <button
              onClick={onExport}
              disabled={selectedLeads.size === 0}
              className="flex items-center gap-2 px-4 py-2 border border-gray-200 rounded-lg text-sm hover:border-purple-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <Download className="w-4 h-4" />
              导出CSV
            </button>
          </div>
        </div>
      </div>

      {/* 线索列表 */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <table className="w-full">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="py-3 px-4 w-10">
                <input
                  type="checkbox"
                  checked={selectedLeads.size === leads.length && leads.length > 0}
                  onChange={toggleSelectAll}
                  className="rounded border-gray-300"
                />
              </th>
              <th className="text-left py-3 px-4 text-xs font-medium text-gray-500">商户信息</th>
              <th className="text-left py-3 px-4 text-xs font-medium text-gray-500">企业情报</th>
              <th className="text-left py-3 px-4 text-xs font-medium text-gray-500">业务趋势</th>
              <th className="text-left py-3 px-4 text-xs font-medium text-gray-500">AI评分</th>
              <th className="text-left py-3 px-4 text-xs font-medium text-gray-500">状态</th>
              <th className="text-left py-3 px-4 text-xs font-medium text-gray-500">操作</th>
            </tr>
          </thead>
          <tbody>
            {leads.map((lead) => (
              <tr key={lead.id} className="border-b border-gray-100 hover:bg-gray-50 transition-colors">
                <td className="py-4 px-4">
                  <input
                    type="checkbox"
                    checked={selectedLeads.has(lead.id)}
                    onChange={() => toggleLeadSelection(lead.id)}
                    className="rounded border-gray-300"
                  />
                </td>
                <td className="py-4 px-4">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-purple-100 to-blue-100 flex items-center justify-center text-lg">
                      {BUSINESS_TYPE_MAP[lead.type].icon}
                    </div>
                    <div>
                      <h4 className="font-medium text-gray-900">{lead.name}</h4>
                      <p className="text-xs text-gray-500">{lead.area}</p>
                      <p className="text-xs text-gray-400 truncate max-w-48">{lead.address}</p>
                      {lead.contact && (
                        <p className="text-xs text-gray-400 mt-0.5">{lead.contact} · {lead.phone}</p>
                      )}
                    </div>
                  </div>
                </td>
                <td className="py-4 px-4">
                  <div className="space-y-1 text-sm">
                    <div className="flex items-center gap-1">
                      <Star className="w-3.5 h-3.5 text-yellow-500" />
                      <span>{lead.rating} ({lead.reviewCount}评)</span>
                    </div>
                    {lead.avgPrice && (
                      <div className="flex items-center gap-1 text-gray-600">
                        <DollarSign className="w-3.5 h-3.5" />
                        <span>{lead.avgPrice}</span>
                      </div>
                    )}
                    <div className="flex items-center gap-1 text-gray-600">
                      <Activity className="w-3.5 h-3.5" />
                      <span>周访问 {lead.weeklyVisits}</span>
                    </div>
                    {lead.recruitCount > 0 && (
                      <div className="flex items-center gap-1 text-green-600">
                        <Briefcase className="w-3.5 h-3.5" />
                        <span>招{lead.recruitCount}人 {lead.recruitAvgSalary}</span>
                      </div>
                    )}
                  </div>
                </td>
                <td className="py-4 px-4">
                  <div className="flex items-center gap-2">
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                      lead.businessTrend === 'expanding' ? 'bg-green-100' :
                      lead.businessTrend === 'stable' ? 'bg-blue-100' :
                      'bg-red-100'
                    }`}>
                      {(() => {
                        const Icon = TREND_MAP[lead.businessTrend].icon;
                        return <Icon className={`w-4 h-4 ${
                          lead.businessTrend === 'expanding' ? 'text-green-600' :
                          lead.businessTrend === 'stable' ? 'text-blue-600' :
                          'text-red-600'
                        }`} />;
                      })()}
                    </div>
                    <div>
                      <p className={`text-sm font-medium ${
                        lead.businessTrend === 'expanding' ? 'text-green-700' :
                        lead.businessTrend === 'stable' ? 'text-blue-700' :
                        'text-red-700'
                      }`}>
                        {TREND_MAP[lead.businessTrend].label}
                      </p>
                      <p className="text-xs text-gray-400">
                        {lead.businessTrend === 'expanding' ? '🔥 优先触达' :
                         lead.businessTrend === 'stable' ? '稳定经营' :
                         '谨慎合作'}
                      </p>
                    </div>
                  </div>
                </td>
                <td className="py-4 px-4">
                  <div className="text-center">
                    <p className={`text-2xl font-bold ${
                      lead.score >= 90 ? 'text-purple-600' :
                      lead.score >= 80 ? 'text-green-600' :
                      lead.score >= 60 ? 'text-yellow-600' :
                      'text-red-600'
                    }`}>
                      {lead.score}
                    </p>
                    <p className="text-xs text-gray-400">综合评分</p>
                  </div>
                </td>
                <td className="py-4 px-4">
                  <span className={`px-2 py-1 rounded-full text-xs ${
                    lead.status === 'pending' ? 'bg-gray-100 text-gray-700' :
                    lead.status === 'contacted' ? 'bg-blue-100 text-blue-700' :
                    lead.status === 'following' ? 'bg-yellow-100 text-yellow-700' :
                    lead.status === 'converted' ? 'bg-green-100 text-green-700' :
                    'bg-red-100 text-red-700'
                  }`}>
                    {lead.status === 'pending' ? '待联系' :
                     lead.status === 'contacted' ? '已联系' :
                     lead.status === 'following' ? '跟进中' :
                     lead.status === 'converted' ? '已转化' : '已流失'}
                  </span>
                  {lead.lastContact && (
                    <p className="text-xs text-gray-400 mt-1">上次: {lead.lastContact}</p>
                  )}
                </td>
                <td className="py-4 px-4">
                  <div className="flex items-center gap-1">
                    <button 
                      onClick={() => alert(`查看详情: ${lead.name}`)}
                      className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                      title="查看详情"
                    >
                      <Eye className="w-4 h-4" />
                    </button>
                    <button 
                      onClick={() => alert(`打电话: ${lead.phone}`)}
                      className="p-2 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                      title="打电话"
                    >
                      <Phone className="w-4 h-4" />
                    </button>
                    <button 
                      onClick={() => alert(`发私信给: ${lead.name}`)}
                      className="p-2 text-gray-400 hover:text-purple-600 hover:bg-purple-50 rounded-lg transition-colors"
                      title="发私信"
                    >
                      <MessageSquare className="w-4 h-4" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// 4. 地图选点
const MapView: React.FC<{ leads: Lead[] }> = ({ leads }) => {
  const [radius, setRadius] = useState(5);

  return (
    <div className="max-w-7xl mx-auto h-[calc(100vh-200px)]">
      <div className="bg-white rounded-xl border border-gray-200 h-full flex overflow-hidden">
        {/* 侧边栏 */}
        <div className="w-80 border-r border-gray-200 p-4 overflow-y-auto">
          <h3 className="font-semibold text-gray-900 mb-4">地图选点拓客</h3>
          
          {/* 中心点设置 */}
          <div className="space-y-3 mb-6">
            <label className="text-sm text-gray-600">中心位置</label>
            <div className="relative">
              <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                defaultValue="北京市朝阳区国贸"
                className="w-full pl-9 pr-4 py-2 border border-gray-200 rounded-lg text-sm"
              />
            </div>
          </div>

          {/* 半径调节 */}
          <div className="space-y-3 mb-6">
            <div className="flex items-center justify-between">
              <label className="text-sm text-gray-600">搜索半径</label>
              <span className="text-sm font-medium text-purple-600">{radius}km</span>
            </div>
            <input
              type="range"
              min="1"
              max="20"
              value={radius}
              onChange={(e) => setRadius(parseInt(e.target.value))}
              className="w-full accent-purple-600"
            />
            <div className="flex justify-between text-xs text-gray-400">
              <span>1km</span>
              <span>20km</span>
            </div>
          </div>

          {/* 商户类型 */}
          <div className="space-y-3 mb-6">
            <label className="text-sm text-gray-600">目标商户类型</label>
            <div className="space-y-2">
              {Object.entries(BUSINESS_TYPE_MAP).slice(0, 6).map(([key, value]) => (
                <label key={key} className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" defaultChecked className="rounded border-gray-300 accent-purple-600" />
                  <span className="text-sm text-gray-700">{value.icon} {value.label}</span>
                </label>
              ))}
            </div>
          </div>

          {/* 统计 */}
          <div className="bg-purple-50 rounded-lg p-4">
            <p className="text-sm text-purple-900 font-medium mb-2">覆盖范围统计</p>
            <div className="space-y-1 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600">覆盖半径</span>
                <span className="font-medium">{radius}km</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">预估覆盖商户</span>
                <span className="font-medium">{Math.floor(radius * 15)}+</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">预计耗时</span>
                <span className="font-medium">{Math.floor(radius * 2)}分钟</span>
              </div>
            </div>
          </div>

          <button 
            onClick={() => alert(`开始抓取 ${radius}km 范围内的商户数据`)}
            className="w-full mt-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
          >
            启动爬虫任务
          </button>
        </div>

        {/* 地图区域 */}
        <div className="flex-1 relative bg-gray-50">
          <div className="absolute inset-4 bg-white rounded-xl border border-gray-200 overflow-hidden">
            {/* 模拟地图背景 */}
            <div className="relative w-full h-full bg-gradient-to-br from-blue-50 to-green-50">
              {/* 网格 */}
              <div className="absolute inset-0" style={{ 
                backgroundImage: 'linear-gradient(#e5e7eb 1px, transparent 1px), linear-gradient(90deg, #e5e7eb 1px, transparent 1px)',
                backgroundSize: '60px 60px'
              }}></div>
              
              {/* 搜索范围圈 */}
              <div 
                className="absolute border-2 border-purple-500 border-dashed rounded-full bg-purple-500/10"
                style={{
                  width: `${radius * 15}px`,
                  height: `${radius * 15}px`,
                  left: '50%',
                  top: '50%',
                  transform: 'translate(-50%, -50%)',
                  transition: 'all 0.3s ease'
                }}
              >
                <span className="absolute -top-6 left-1/2 -translate-x-1/2 text-xs text-purple-600 font-medium bg-white px-2 py-0.5 rounded">
                  {radius}km
                </span>
              </div>

              {/* 中心点 */}
              <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
                <div className="w-4 h-4 bg-purple-600 rounded-full border-2 border-white shadow-lg"></div>
                <div className="absolute top-5 left-1/2 -translate-x-1/2 whitespace-nowrap">
                  <span className="text-xs text-purple-700 font-medium bg-white px-2 py-0.5 rounded shadow">中心点</span>
                </div>
              </div>

              {/* 商户标记 */}
              {leads.slice(0, 10).map((lead) => (
                <div
                  key={lead.id}
                  className="absolute cursor-pointer group"
                  style={{
                    left: `${30 + Math.random() * 40}%`,
                    top: `${20 + Math.random() * 60}%`,
                  }}
                >
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold shadow-lg transition-transform group-hover:scale-110 ${
                    lead.businessTrend === 'expanding' ? 'bg-green-500' :
                    lead.businessTrend === 'stable' ? 'bg-blue-500' :
                    'bg-red-500'
                  }`}>
                    {BUSINESS_TYPE_MAP[lead.type].icon}
                  </div>
                  <div className="absolute top-full left-1/2 -translate-x-1/2 mt-1 px-2 py-1 bg-gray-800 text-white text-xs rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity z-10">
                    {lead.name} ({lead.score}分)
                  </div>
                </div>
              ))}

              {/* 图例 */}
              <div className="absolute bottom-4 right-4 bg-white/90 rounded-lg p-3 shadow-lg">
                <p className="text-xs font-medium text-gray-700 mb-2">图例</p>
                <div className="space-y-1">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-green-500"></div>
                    <span className="text-xs text-gray-600">扩张期</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-blue-500"></div>
                    <span className="text-xs text-gray-600">稳定期</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-red-500"></div>
                    <span className="text-xs text-gray-600">下滑期</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// 5. 触达模板
const MessagesView: React.FC<{ templates: MessageTemplate[] }> = ({ templates }) => {
  const [activeTemplate, setActiveTemplate] = useState<MessageTemplate>(templates[0]);

  // 预览模板（替换变量）
  const previewTemplate = (template: MessageTemplate) => {
    let content = template.content;
    content = content.replace(/{{name}}/g, '张经理');
    content = content.replace(/{{company}}/g, 'XX视光中心');
    content = content.replace(/{{industry}}/g, '早教');
    content = content.replace(/{{similar_company}}/g, '金宝贝');
    return content;
  };

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">私信模板管理</h2>
          <p className="text-sm text-gray-500 mt-1">管理批量触达话术模板，提升回复率</p>
        </div>
        <button 
          onClick={() => alert('打开新建模板表单')}
          className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
        >
          <Plus className="w-4 h-4" />
          新建模板
        </button>
      </div>

      <div className="grid grid-cols-3 gap-6">
        {/* 模板列表 */}
        <div className="space-y-3">
          <h3 className="font-medium text-gray-900">模板列表</h3>
          {templates.map((template) => (
            <button
              key={template.id}
              onClick={() => setActiveTemplate(template)}
              className={`w-full text-left p-4 rounded-xl border transition-all ${
                activeTemplate.id === template.id
                  ? 'border-purple-500 bg-purple-50'
                  : 'border-gray-200 hover:border-purple-300 bg-white'
              }`}
            >
              <div className="flex items-center justify-between mb-2">
                <span className={`px-2 py-0.5 rounded text-xs ${
                  template.type === 'first' ? 'bg-blue-100 text-blue-700' :
                  template.type === 'follow' ? 'bg-yellow-100 text-yellow-700' :
                  'bg-green-100 text-green-700'
                }`}>
                  {template.type === 'first' ? '首次接触' :
                   template.type === 'follow' ? '跟进' : '转化'}
                </span>
                <span className="text-xs text-gray-500">回复率 {Math.round(template.replyRate * 100)}%</span>
              </div>
              <h4 className="font-medium text-gray-900">{template.name}</h4>
              <p className="text-xs text-gray-500 mt-1">{template.variables.length} 个变量</p>
            </button>
          ))}
        </div>

        {/* 模板详情 */}
        <div className="col-span-2 space-y-4">
          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="font-semibold text-gray-900">{activeTemplate.name}</h3>
                <div className="flex items-center gap-2 mt-1">
                  <span className="text-xs text-gray-500">变量:</span>
                  {activeTemplate.variables.map((v) => (
                    <span key={v} className="px-2 py-0.5 bg-purple-100 text-purple-700 rounded text-xs">
                      {v}
                    </span>
                  ))}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button 
                  onClick={() => alert('编辑模板')}
                  className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                >
                  <Edit3 className="w-4 h-4" />
                </button>
                <button 
                  onClick={() => alert('删除模板')}
                  className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>

            <div className="space-y-4">
              <div>
                <label className="text-sm text-gray-600 mb-2 block">模板内容</label>
                <textarea
                  value={activeTemplate.content}
                  readOnly
                  rows={6}
                  className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm font-mono bg-gray-50 resize-none"
                />
              </div>

              <div>
                <label className="text-sm text-gray-600 mb-2 block">预览效果</label>
                <div className="bg-gray-50 rounded-lg p-4 text-sm whitespace-pre-wrap">
                  {previewTemplate(activeTemplate)}
                </div>
              </div>
            </div>
          </div>

          {/* 变量说明 */}
          <div className="bg-gradient-to-r from-purple-50 to-blue-50 rounded-xl border border-purple-200 p-5">
            <h4 className="font-semibold text-purple-900 mb-3">可用变量说明</h4>
            <div className="grid grid-cols-2 gap-3 text-sm">
              {[
                { name: '{{name}}', desc: '联系人姓名' },
                { name: '{{company}}', desc: '公司名称' },
                { name: '{{industry}}', desc: '行业类型' },
                { name: '{{similar_company}}', desc: '相似案例公司' },
                { name: '{{platform}}', desc: '平台名称' },
                { name: '{{topic}}', desc: '上次话题' },
              ].map((v) => (
                <div key={v.name} className="flex items-center gap-2">
                  <code className="px-2 py-1 bg-white rounded text-purple-700 text-xs">{v.name}</code>
                  <span className="text-gray-600">{v.desc}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ==================== 弹窗组件 ====================

// 新建任务弹窗
const NewTaskModal: React.FC<{ onClose: () => void; onCreate: (task: CrawlerTask) => void }> = ({ onClose, onCreate }) => {
  const [formData, setFormData] = useState({
    name: '',
    centerAddress: '',
    radius: 5,
    businessTypes: [] as BusinessType[],
    maxResults: 50,
  });

  const handleCreate = () => {
    const task: CrawlerTask = {
      id: `T${Date.now()}`,
      name: formData.name || `拓客任务-${new Date().toLocaleDateString()}`,
      centerAddress: formData.centerAddress || '北京市朝阳区',
      radius: formData.radius,
      businessTypes: formData.businessTypes.length > 0 ? formData.businessTypes : ['maternity', 'education'],
      maxResults: formData.maxResults,
      status: 'running',
      progress: 0,
      leadsFound: 0,
      createdAt: new Date().toLocaleString('zh-CN'),
    };
    onCreate(task);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">新建爬虫任务</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-6 overflow-y-auto max-h-[60vh]">
          {/* 任务名称 */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">任务名称</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="如：CBD商圈拓客任务"
              className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-purple-500"
            />
          </div>

          {/* 中心位置 */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">中心位置</label>
            <div className="relative">
              <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                value={formData.centerAddress}
                onChange={(e) => setFormData({ ...formData, centerAddress: e.target.value })}
                placeholder="输入地址或在地图选点"
                className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-purple-500"
              />
            </div>
          </div>

          {/* 搜索半径 */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium text-gray-700">搜索半径</label>
              <span className="text-sm text-purple-600 font-medium">{formData.radius}km</span>
            </div>
            <input
              type="range"
              min="1"
              max="20"
              value={formData.radius}
              onChange={(e) => setFormData({ ...formData, radius: parseInt(e.target.value) })}
              className="w-full accent-purple-600"
            />
            <div className="flex justify-between text-xs text-gray-400 mt-1">
              <span>1km</span>
              <span>10km</span>
              <span>20km</span>
            </div>
          </div>

          {/* 商户类型 */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">目标商户类型</label>
            <div className="grid grid-cols-3 gap-3">
              {Object.entries(BUSINESS_TYPE_MAP).map(([key, value]) => (
                <label
                  key={key}
                  className={`flex items-center gap-3 p-3 border rounded-lg cursor-pointer transition-colors ${
                    formData.businessTypes.includes(key as BusinessType)
                      ? 'border-purple-500 bg-purple-50'
                      : 'border-gray-200 hover:border-purple-300'
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={formData.businessTypes.includes(key as BusinessType)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setFormData({ ...formData, businessTypes: [...formData.businessTypes, key as BusinessType] });
                      } else {
                        setFormData({ ...formData, businessTypes: formData.businessTypes.filter(t => t !== key) });
                      }
                    }}
                    className="rounded border-gray-300 accent-purple-600"
                  />
                  <span className="text-lg">{value.icon}</span>
                  <span className="text-sm text-gray-700">{value.label}</span>
                </label>
              ))}
            </div>
          </div>

          {/* 最大结果数 */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">最大结果数</label>
            <select
              value={formData.maxResults}
              onChange={(e) => setFormData({ ...formData, maxResults: parseInt(e.target.value) })}
              className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-purple-500"
            >
              <option value={20}>20条（快速测试）</option>
              <option value={50}>50条（推荐）</option>
              <option value={100}>100条（深度拓客）</option>
              <option value={200}>200条（大规模）</option>
            </select>
          </div>
        </div>

        <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-gray-200 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
          >
            取消
          </button>
          <button
            onClick={handleCreate}
            className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
          >
            启动任务
          </button>
        </div>
      </div>
    </div>
  );
};

// 批量私信弹窗
const MessageModal: React.FC<{
  selectedCount: number;
  templates: MessageTemplate[];
  selectedTemplate: MessageTemplate;
  onSelectTemplate: (t: MessageTemplate) => void;
  onClose: () => void;
  onSend: () => void;
}> = ({ selectedCount, templates, selectedTemplate, onSelectTemplate, onClose, onSend }) => {
  const [content, setContent] = useState(selectedTemplate.content);

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">批量私信触达</h3>
            <p className="text-sm text-gray-500 mt-1">已选择 {selectedCount} 个目标客户</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-4 overflow-y-auto max-h-[60vh]">
          {/* 模板选择 */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">选择模板</label>
            <div className="grid grid-cols-2 gap-3">
              {templates.map((template) => (
                <button
                  key={template.id}
                  onClick={() => {
                    onSelectTemplate(template);
                    setContent(template.content);
                  }}
                  className={`p-3 border rounded-lg text-left transition-colors ${
                    selectedTemplate.id === template.id
                      ? 'border-purple-500 bg-purple-50'
                      : 'border-gray-200 hover:border-purple-300'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <span className="font-medium text-sm">{template.name}</span>
                    <span className="text-xs text-gray-500">回复率 {Math.round(template.replyRate * 100)}%</span>
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* 消息内容 */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">消息内容</label>
            <textarea
              value={content}
              onChange={(e) => setContent(e.target.value)}
              rows={8}
              className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm resize-none focus:outline-none focus:border-purple-500"
            />
            <p className="text-xs text-gray-400 mt-1">支持变量: {'{{name}}'}, {'{{company}}'}, {'{{industry}}'}, {'{{similar_company}}'}</p>
          </div>

          {/* 发送设置 */}
          <div className="bg-gray-50 rounded-lg p-4">
            <h4 className="font-medium text-gray-900 mb-3">发送设置</h4>
            <div className="space-y-3 text-sm">
              <label className="flex items-center gap-2">
                <input type="checkbox" defaultChecked className="rounded border-gray-300 accent-purple-600" />
                <span>自动替换变量（{'{{name}}'}等）</span>
              </label>
              <label className="flex items-center gap-2">
                <input type="checkbox" defaultChecked className="rounded border-gray-300 accent-purple-600" />
                <span>发送后自动标记为"已联系"</span>
              </label>
              <label className="flex items-center gap-2">
                <input type="checkbox" className="rounded border-gray-300 accent-purple-600" />
                <span>分批发送（间隔5分钟，防封禁）</span>
              </label>
            </div>
          </div>
        </div>

        <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-gray-200 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
          >
            取消
          </button>
          <button
            onClick={onSend}
            className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors flex items-center gap-2"
          >
            <Send className="w-4 h-4" />
            发送给 {selectedCount} 个客户
          </button>
        </div>
      </div>
    </div>
  );
};

export default OpenClawModule;
